import { useParams, Link } from 'react-router-dom';
import { useMemo, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const movie = useMemo(() => {
    return movies.find((m) => m.slug === slug);
  }, [slug]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
      .slice(0, 4);
  }, [movie]);

  if (!movie) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-10 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-300"></i>
          </div>
          <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
            影片未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            抱歉，您要查看的影片不存在或已被移除。
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: movie.category, path: `/category/${movie.categorySlug}` },
    { name: movie.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: movie.title,
    description: movie.description,
    image: movie.image,
    datePublished: `${movie.year}-01-01`,
    author: {
      '@type': 'Person',
      name: movie.director,
    },
    publisher: {
      '@type': 'Organization',
      name: '91精品视频在线播放',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91jingpin.com/article/${movie.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <article className="mt-6">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="flex-shrink-0 lg:w-72">
                    <div className="sticky top-24">
                      <div className="relative rounded-lg overflow-hidden aspect-[2/3]">
                        <img
                          src={movie.image}
                          alt={movie.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold bg-primary-500 text-background-50">
                            {movie.rating}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                      {movie.title}
                    </h1>

                    <div className="flex items-center gap-3 flex-wrap mb-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-secondary-100 text-secondary-700">
                        {movie.category}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-secondary-100 text-secondary-700">
                        {movie.type === 'movie' ? '电影' : '剧集'}
                      </span>
                      {movie.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-accent-100 text-accent-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 p-4 rounded-lg bg-background-100">
                      <div>
                        <p className="text-xs text-foreground-500">上映年份</p>
                        <p className="text-sm font-medium text-foreground-900">{movie.year}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">片长/集数</p>
                        <p className="text-sm font-medium text-foreground-900">{movie.duration}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">导演</p>
                        <p className="text-sm font-medium text-foreground-900">{movie.director}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">评分</p>
                        <p className="text-sm font-medium text-primary-600">{movie.rating}</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h2 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                        主演
                      </h2>
                      <div className="flex items-center gap-2 flex-wrap">
                        {movie.actors.map((actor) => (
                          <span
                            key={actor}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm bg-background-100 text-foreground-700"
                          >
                            {actor}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h2 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                        剧情简介
                      </h2>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {movie.description}
                      </p>
                    </div>

                    <div>
                      <h2 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                        详细剧情
                      </h2>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {movie.fullDescription}
                      </p>
                    </div>
                  </div>
                </div>
              </article>

              {relatedMovies.length > 0 && (
                <section className="mt-12 pt-8 border-t border-background-200">
                  <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                    同类推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {relatedMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}